import { useMemo, useState } from "react";

import {
    AbfLoanExpanded,
    BsMetaUtil,
    formatLeverage,
    calculateNetApyLive,
    calculateHealthRatio,
    calculateLoanHealth,
    calculateLiquidationPrice,
    getHealthStatus,
    getLoanCollateralMarketUsdValue,
    getZcLedger,
    getZcLoanCollateral,
    getZcLoanLtv,
    LoopPositionExpanded,
    getLoopSlippage,
    useLoopPnlDisplayValues
} from "@bridgesplit/abf-react";
import { bsMath, formatDate, formatPercent, formatTokenAmount, formatUsd } from "@bridgesplit/utils";
import { Column, Row, StatColumn, StatProps, Text, ToggleChevron, TextVariant } from "@bridgesplit/ui";
import { Collapse, Divider } from "@mui/material";
import { ArrowForwardOutlined } from "@mui/icons-material";
import { COPY } from "app/constants/copy";

import { HealthDetails, HealthText, LtvText, useLoanOraclePriceQuotes, LoopTokenUsdToggle } from "../../common";
import { ExecutionDetails } from "../../loop/execution-detail";
import { LoopExecutionDetails } from "../../loop/execution-detail/types";

export type LoanPositionStatsShowConfig = {
    debtAndCollateral?: boolean;
    leverageCollapsable?: boolean;
    health?: boolean;
    netApyStat?: boolean;
    netApyCollapsable?: boolean;
    borrowApy?: boolean;
    executionDetails?: boolean;
    pnl?: boolean;
};

export type PositionData = {
    // Debt
    originalDebt: number;
    newDebt: number;
    principalSymbol: string;

    // Collateral
    originalCollateralAmount: number;
    newCollateralAmount: number;
    collateralSymbol: string;
    originalCollateralValueUsd: number;
    newCollateralValueUsd: number;
    displayCollateralAsUsd: boolean;

    // Due date
    newDueDate?: number;

    // Health
    originalHealth?: HealthDetails;
    newHealth?: HealthDetails;
    originalLtv?: number;
    newLtv?: number;
    liquidationThreshold?: number;
    strategyLtv?: number;
    originalLiquidationPrice?: number;
    newLiquidationPrice?: number;
    principalUsdPrice?: number;

    // Rates
    originalBorrowApy?: number;
    newBorrowApy?: number;
    originalNetApy?: number;
    newNetApy?: number;
    collateralApy?: number;
};

export type LoanPositionStatsProps = {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    newPrincipalAmount: number;
    newCollateralAmount: number;
    isFullRepay: boolean;
    loading: boolean;
    textVariant?: TextVariant;
    newDueDate?: number;
    newBorrowApy?: number;
    executionDetails?: LoopExecutionDetails | undefined;
    newStrategyLtv?: number;
    newLiquidationThreshold?: number;
    newCollateralValueUsd?: number;
    startingLeverage?: number;
    newLeverage?: number;
    show?: LoanPositionStatsShowConfig;
};
const iconAndChevronVariant = "body1";

export function LoanPositionStats(props: LoanPositionStatsProps) {
    const {
        loanExpanded,
        loopPosition,
        newPrincipalAmount,
        newCollateralAmount,
        isFullRepay,
        loading,
        textVariant = "body2",
        newDueDate,
        newBorrowApy,
        executionDetails,
        newStrategyLtv,
        newLiquidationThreshold,
        newCollateralValueUsd,
        startingLeverage,
        newLeverage,
        show
    } = props;

    const showConfig: Required<LoanPositionStatsShowConfig> = {
        debtAndCollateral: show?.debtAndCollateral ?? true,
        leverageCollapsable: show?.leverageCollapsable ?? false,
        health: show?.health ?? false,
        netApyStat: show?.netApyStat ?? false,
        netApyCollapsable: show?.netApyCollapsable ?? false,
        borrowApy: show?.borrowApy ?? false,
        executionDetails: show?.executionDetails ?? true,
        pnl: show?.pnl ?? false
    };

    const data = usePositionData({
        loanExpanded,
        newPrincipalAmount,
        newCollateralAmount,
        isFullRepay,
        newDueDate,
        newBorrowApy,
        newStrategyLtv,
        newLiquidationThreshold,
        newCollateralValueUsd
    });
    const slippagePresetsUi = getLoopSlippage(loopPosition?.loopExpanded).slippagePresetsUi;

    const hasDebtOrLeverage = showConfig.debtAndCollateral || showConfig.leverageCollapsable;
    const hasHealthOrApy =
        showConfig.health || showConfig.netApyStat || showConfig.netApyCollapsable || showConfig.borrowApy;

    return (
        <Column spacing={0.5}>
            {showConfig.debtAndCollateral && (
                <DebtAndCollateralSection data={data} loading={loading} textVariant={textVariant} />
            )}

            {showConfig.leverageCollapsable && (
                <LeverageCollapsableSection
                    data={data}
                    loading={loading}
                    textVariant={textVariant}
                    startingLeverage={startingLeverage}
                    newLeverage={newLeverage}
                />
            )}

            {hasDebtOrLeverage && hasHealthOrApy && <Divider />}

            {showConfig.health && <LoanHealthSection data={data} loading={loading} textVariant={textVariant} />}

            {showConfig.netApyStat && <NetApyStatSection data={data} loading={loading} textVariant={textVariant} />}
            {showConfig.netApyCollapsable && (
                <NetApyCollapsableSection data={data} loading={loading} textVariant={textVariant} />
            )}
            {showConfig.borrowApy && <BorrowApySection data={data} loading={loading} textVariant={textVariant} />}

            {showConfig.executionDetails && executionDetails && (
                <ExecutionDetails
                    executionDetails={executionDetails}
                    loading={loading}
                    textVariant={textVariant}
                    slippagePresetsUi={slippagePresetsUi}
                />
            )}
            {showConfig.pnl && <PnlSection loopPosition={loopPosition} loading={loading} textVariant={textVariant} />}
        </Column>
    );
}

function PnlSection({
    loopPosition,
    loading,
    textVariant
}: {
    loopPosition: LoopPositionExpanded | undefined;
    loading: boolean;
    textVariant: TextVariant;
}) {
    const collateral = getZcLoanCollateral(loopPosition?.loanExpanded);
    const { collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loopPosition?.loopExpanded?.principalToken,
        collateral
    );

    const { displayUsdPnl, displayTokenPnl } = useLoopPnlDisplayValues(loopPosition, collateralUsdOraclePrice);

    const pnlStats = loopPosition?.loanExpanded?.pnlStats;

    return (
        <StatColumn
            loading={loading}
            variant={textVariant}
            stats={[
                {
                    caption: COPY.LOOP_PNL_TERM_MOBILE,
                    value: (
                        <LoopTokenUsdToggle
                            usdValue={displayUsdPnl}
                            tokenValue={displayTokenPnl}
                            pctChangeToken={pnlStats?.collateralPnlPctChange}
                            pctChangeUsd={pnlStats?.usdPnlPctChange}
                            tokenMetadata={loopPosition?.loopExpanded?.collateralToken}
                            loopPosition={loopPosition}
                            textVariant={textVariant}
                        />
                    )
                }
            ]}
        />
    );
}

function BorrowApySection({
    data,
    loading,
    textVariant
}: {
    data: PositionData | undefined;
    loading: boolean;
    textVariant?: TextVariant;
}) {
    if (!data) return null;

    const { originalBorrowApy, newBorrowApy } = data;
    const borrowApyChanged = formatPercent(originalBorrowApy) !== formatPercent(newBorrowApy);

    return (
        <StatColumn
            loading={loading}
            variant={textVariant}
            stats={[
                {
                    caption: "Borrow APY",
                    value: borrowApyChanged ? (
                        <ChangeValue
                            alwaysShowArrow
                            originalValue={formatPercent(originalBorrowApy)}
                            newValue={formatPercent(newBorrowApy)}
                            loading={loading}
                            variant={textVariant}
                        />
                    ) : (
                        <Text variant={textVariant} loading={loading}>
                            {formatPercent(newBorrowApy ?? originalBorrowApy)}
                        </Text>
                    )
                }
            ]}
        />
    );
}

function DebtAndCollateralSection({
    data,
    loading,
    textVariant
}: {
    data: PositionData | undefined;
    loading: boolean;
    textVariant?: TextVariant;
}) {
    if (!data) return null;

    const {
        originalDebt,
        newDebt,
        principalSymbol,
        originalCollateralAmount,
        newCollateralAmount,
        collateralSymbol,
        originalCollateralValueUsd,
        newCollateralValueUsd,
        displayCollateralAsUsd,
        newDueDate
    } = data;

    const originalDebtFormatted = formatTokenAmount(originalDebt, {
        symbol: principalSymbol,
        hideSymbol: originalDebt !== newDebt
    });
    const newDebtFormatted = formatTokenAmount(newDebt, { symbol: principalSymbol });

    const originalCollateralFormatted = formatCollateralAmount({
        amount: originalCollateralAmount,
        symbol: collateralSymbol,
        valueUsd: originalCollateralValueUsd,
        displayAsUsd: displayCollateralAsUsd,
        hideSymbol: originalCollateralAmount !== newCollateralAmount
    });
    const newCollateralFormatted = formatCollateralAmount({
        amount: newCollateralAmount,
        symbol: collateralSymbol,
        valueUsd: newCollateralValueUsd,
        displayAsUsd: displayCollateralAsUsd
    });
    const collateralChanged = originalCollateralFormatted !== newCollateralFormatted;

    const stats: StatProps[] = [
        {
            caption: "Debt",
            value: (
                <ChangeValue originalValue={originalDebtFormatted} newValue={newDebtFormatted} variant={textVariant} />
            )
        },
        {
            caption: displayCollateralAsUsd ? "Collateral value" : "Collateral",
            value: collateralChanged ? (
                <ChangeValue
                    originalValue={originalCollateralFormatted}
                    newValue={newCollateralFormatted}
                    variant={textVariant}
                />
            ) : (
                <Text variant={textVariant}>{newCollateralFormatted}</Text>
            )
        },
        {
            caption: "Rate fixed until",
            hide: !newDueDate,
            value: (
                <ChangeValue
                    originalValue={formatDate(newDueDate)}
                    newValue={formatDate(newDueDate)}
                    variant={textVariant}
                />
            )
        }
    ];

    return <StatColumn loading={loading} stats={stats} variant={textVariant} />;
}

function formatCollateralAmount({
    amount,
    symbol,
    valueUsd,
    displayAsUsd,
    hideSymbol
}: {
    amount: number;
    symbol: string;
    valueUsd: number;
    displayAsUsd: boolean;
    hideSymbol?: boolean;
}) {
    if (displayAsUsd) return formatUsd(valueUsd);
    return formatTokenAmount(amount, { symbol, hideSymbol });
}

function LeverageCollapsableSection({
    data,
    loading,
    textVariant,
    startingLeverage,
    newLeverage
}: {
    data: PositionData | undefined;
    loading: boolean;
    textVariant?: TextVariant;
    startingLeverage: number | undefined;
    newLeverage: number | undefined;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    if (!data) return null;
    if (startingLeverage === undefined) return null;

    const {
        originalDebt,
        newDebt,
        principalSymbol,
        originalCollateralAmount,
        newCollateralAmount,
        collateralSymbol,
        originalCollateralValueUsd,
        newCollateralValueUsd,
        displayCollateralAsUsd
    } = data;

    const leverageChanged = formatLeverage(startingLeverage, 1) !== formatLeverage(newLeverage, 1);

    const originalDebtFormatted = formatTokenAmount(originalDebt, {
        symbol: principalSymbol,
        hideSymbol: originalDebt !== newDebt
    });
    const newDebtFormatted = formatTokenAmount(newDebt, { symbol: principalSymbol });

    const originalCollateralFormatted = formatCollateralAmount({
        amount: originalCollateralAmount,
        symbol: collateralSymbol,
        valueUsd: originalCollateralValueUsd,
        displayAsUsd: displayCollateralAsUsd,
        hideSymbol: originalCollateralAmount !== newCollateralAmount
    });
    const newCollateralFormatted = formatCollateralAmount({
        amount: newCollateralAmount,
        symbol: collateralSymbol,
        valueUsd: newCollateralValueUsd,
        displayAsUsd: displayCollateralAsUsd
    });
    const collateralChanged = originalCollateralFormatted !== newCollateralFormatted;

    const stats: StatProps[] = [
        {
            caption: "Debt",
            value: (
                <ChangeValue originalValue={originalDebtFormatted} newValue={newDebtFormatted} variant={textVariant} />
            )
        },
        {
            caption: displayCollateralAsUsd ? "Collateral value" : "Collateral",
            value: collateralChanged ? (
                <ChangeValue
                    originalValue={originalCollateralFormatted}
                    newValue={newCollateralFormatted}
                    variant={textVariant}
                />
            ) : (
                <Text variant={textVariant}>{newCollateralFormatted}</Text>
            )
        }
    ];

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Row spacing={1}>
                    <Text color="caption" variant={textVariant} loading={loading}>
                        Leverage
                    </Text>
                </Row>
                <Row spacing={1}>
                    {loading || !leverageChanged ? (
                        <Text variant={textVariant} loading={loading}>
                            {formatLeverage(newLeverage ?? startingLeverage, 1)}
                        </Text>
                    ) : (
                        <ChangeValue
                            originalValue={formatLeverage(startingLeverage, 1)}
                            newValue={formatLeverage(newLeverage, 1)}
                            alwaysShowArrow
                            equalCaptionForNew
                            variant={textVariant}
                        />
                    )}
                    <ToggleChevron
                        onClick={() => setIsExpanded(!isExpanded)}
                        textVariant={iconAndChevronVariant}
                        sx={{ p: 0 }}
                        expanded={isExpanded}
                    />
                </Row>
            </Row>
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 0.5 }} spacing={0.5}>
                    <Divider />
                    <StatColumn loading={loading} variant={textVariant} spacing={0.5} stats={stats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function LoanHealthSection({
    data,
    loading,
    textVariant
}: {
    data: PositionData | undefined;
    loading: boolean;
    textVariant?: TextVariant;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    if (!data) return null;

    const {
        originalHealth,
        newHealth,
        originalLtv,
        newLtv,
        liquidationThreshold,
        strategyLtv,
        originalLiquidationPrice,
        newLiquidationPrice,
        principalUsdPrice
    } = data;
    if (!originalHealth || !newHealth || originalLtv === undefined || newLtv === undefined) return null;

    const healthChanged = formatPercent(originalHealth.health) !== formatPercent(newHealth.health);

    const boundedNewLtv =
        liquidationThreshold !== undefined ? Math.min(newLtv ?? Infinity, liquidationThreshold ?? Infinity) : newLtv;
    const ltvChanged =
        liquidationThreshold !== undefined && formatPercent(originalLtv) !== formatPercent(boundedNewLtv ?? 0);

    const originalLiqPriceUsd =
        originalLiquidationPrice && principalUsdPrice
            ? bsMath.mul(originalLiquidationPrice, principalUsdPrice)
            : undefined;
    const newLiqPriceUsd =
        newLiquidationPrice && principalUsdPrice ? bsMath.mul(newLiquidationPrice, principalUsdPrice) : undefined;
    const liqPriceChanged =
        originalLiqPriceUsd !== undefined &&
        newLiqPriceUsd !== undefined &&
        formatUsd(originalLiqPriceUsd) !== formatUsd(newLiqPriceUsd);

    const stats: StatProps[] =
        liquidationThreshold !== undefined && strategyLtv !== undefined
            ? [
                  {
                      caption: "Liquidation LTV",
                      value: formatPercent(liquidationThreshold)
                  },
                  {
                      caption: "Position LTV",
                      value: (
                          <Row spacing={1}>
                              {!ltvChanged ? (
                                  <LtvText
                                      strategyLtv={strategyLtv}
                                      variant={textVariant}
                                      ltv={boundedNewLtv ?? 0}
                                      liquidationThreshold={liquidationThreshold}
                                  />
                              ) : (
                                  <>
                                      <Text color="caption" variant={textVariant}>
                                          {formatPercent(originalLtv)}
                                      </Text>
                                      <Text color="disabled" variant={iconAndChevronVariant}>
                                          <ArrowForwardOutlined />
                                      </Text>
                                      <LtvText
                                          strategyLtv={strategyLtv}
                                          variant={textVariant}
                                          ltv={boundedNewLtv ?? 0}
                                          liquidationThreshold={liquidationThreshold}
                                      />
                                  </>
                              )}
                          </Row>
                      )
                  },
                  {
                      caption: "Liquidation price",
                      hide: !originalLiqPriceUsd,
                      value: liqPriceChanged ? (
                          <Row spacing={1}>
                              <Text color="caption" variant={textVariant}>
                                  {formatUsd(originalLiqPriceUsd)}
                              </Text>
                              <Text color="disabled" variant={iconAndChevronVariant}>
                                  <ArrowForwardOutlined />
                              </Text>
                              <Text variant={textVariant}>{formatUsd(newLiqPriceUsd)}</Text>
                          </Row>
                      ) : (
                          <Text variant={textVariant}>{formatUsd(originalLiqPriceUsd)}</Text>
                      )
                  }
              ]
            : [];

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Row spacing={1}>
                    <Text color="caption" variant={textVariant} loading={loading}>
                        Health
                    </Text>
                </Row>
                <Row spacing={1}>
                    {!healthChanged || loading ? (
                        <HealthText variant={textVariant} {...originalHealth} loading={loading} />
                    ) : (
                        <Row spacing={1}>
                            <Text color="caption" variant={textVariant}>
                                {formatPercent(originalHealth.health)}
                            </Text>
                            <Text color="disabled" variant={iconAndChevronVariant}>
                                <ArrowForwardOutlined />
                            </Text>
                            <HealthText variant={textVariant} {...newHealth} loading={loading} />
                        </Row>
                    )}
                    <ToggleChevron
                        onClick={() => setIsExpanded(!isExpanded)}
                        textVariant={iconAndChevronVariant}
                        sx={{ p: 0 }}
                        expanded={isExpanded}
                    />
                </Row>
            </Row>
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 0.5 }} spacing={0.5}>
                    <Divider />
                    <StatColumn loading={loading} variant={textVariant} spacing={0.5} stats={stats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function NetApyStatSection({
    data,
    loading,
    textVariant
}: {
    data: PositionData | undefined;
    loading: boolean;
    textVariant?: TextVariant;
}) {
    if (!data) return null;

    const { originalBorrowApy, newBorrowApy, collateralApy, originalNetApy, newNetApy } = data;
    if (!originalBorrowApy && !newBorrowApy && !collateralApy && !originalNetApy && !newNetApy) {
        return null;
    }

    const netApyChanged = formatPercent(originalNetApy) !== formatPercent(newNetApy);

    return (
        <StatColumn
            loading={loading}
            variant={textVariant}
            stats={[
                {
                    caption: "Net APY",
                    value:
                        !netApyChanged || loading ? (
                            <Text variant={textVariant} loading={loading}>
                                {formatPercent(newNetApy ?? originalNetApy)}
                            </Text>
                        ) : (
                            <ChangeValue
                                originalValue={formatPercent(originalNetApy)}
                                newValue={formatPercent(newNetApy)}
                                alwaysShowArrow
                                equalCaptionForNew
                                variant={textVariant}
                            />
                        )
                }
            ]}
        />
    );
}

function NetApyCollapsableSection({
    data,
    loading,
    textVariant
}: {
    data: PositionData | undefined;
    loading: boolean;
    textVariant?: TextVariant;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const netApyChanged = formatPercent(data?.originalNetApy) !== formatPercent(data?.newNetApy);

    const {
        originalBorrowApy = 0,
        newBorrowApy,
        collateralApy,
        originalNetApy,
        newNetApy,
        collateralSymbol
    } = data ?? {};

    const stats: StatProps[] = useMemo(() => {
        const originalBorrowApyFormatted = formatPercent(originalBorrowApy);
        const newBorrowApyFormatted = formatPercent(newBorrowApy ?? originalBorrowApy);

        const borrowApyChanged = originalBorrowApyFormatted !== newBorrowApyFormatted;

        return [
            {
                caption: "Borrow APY",
                value: borrowApyChanged ? (
                    <ChangeValue
                        variant={textVariant}
                        originalValue={originalBorrowApyFormatted}
                        newValue={newBorrowApyFormatted}
                        alwaysShowArrow
                        equalCaptionForNew
                        loading={loading}
                    />
                ) : (
                    <Text variant={textVariant} loading={loading}>
                        {newBorrowApyFormatted}
                    </Text>
                )
            },
            {
                caption: `${collateralSymbol} APY`,
                value: (
                    <Text variant={textVariant} loading={loading}>
                        {formatPercent(collateralApy)}
                    </Text>
                ),
                hide: !collateralApy
            }
        ];
    }, [originalBorrowApy, newBorrowApy, collateralApy, collateralSymbol, textVariant, loading]);

    // If we don't have any meaningful rate data, don't render anything.
    if (!originalBorrowApy && !newBorrowApy && !collateralApy && !originalNetApy && !newNetApy) {
        return null;
    }

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Row spacing={1}>
                    <Text color="caption" variant={textVariant} loading={loading}>
                        Net APY
                    </Text>
                </Row>
                <Row spacing={1}>
                    {!netApyChanged || loading ? (
                        <Text variant={textVariant} loading={loading}>
                            {formatPercent(newNetApy ?? originalNetApy)}
                        </Text>
                    ) : (
                        <ChangeValue
                            originalValue={formatPercent(originalNetApy)}
                            newValue={formatPercent(newNetApy)}
                            alwaysShowArrow
                            equalCaptionForNew
                            variant={textVariant}
                        />
                    )}
                    <ToggleChevron
                        onClick={() => setIsExpanded(!isExpanded)}
                        textVariant={iconAndChevronVariant}
                        sx={{ p: 0 }}
                        expanded={isExpanded}
                    />
                </Row>
            </Row>
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 0.5 }} spacing={0.5}>
                    <Divider />
                    <StatColumn loading={loading} variant={textVariant} spacing={0.5} stats={stats} />
                </Column>
            </Collapse>
        </Column>
    );
}

// Shared shape for extracting stats via a hook.
export type LoanPositionStatsArgs = {
    loanExpanded: AbfLoanExpanded | undefined;
    newPrincipalAmount: number;
    newCollateralAmount: number;
    isFullRepay: boolean;
    newDueDate?: number;
    newBorrowApy?: number;
    newStrategyLtv?: number;
    newLiquidationThreshold?: number;
    newCollateralValueUsd?: number;
};
export function usePositionData({
    loanExpanded,
    newPrincipalAmount,
    newCollateralAmount,
    isFullRepay,
    newDueDate,
    newBorrowApy,
    newStrategyLtv,
    newLiquidationThreshold,
    newCollateralValueUsd
}: LoanPositionStatsArgs): PositionData | undefined {
    const collateral = getZcLoanCollateral(loanExpanded);

    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loanExpanded?.principalMetadata,
        collateral
    );

    if (!loanExpanded) return undefined;

    const ledger = getZcLedger(loanExpanded);

    const originalDebt = ledger?.ledgerDebt.total ?? 0;
    const newDebt = isFullRepay ? 0 : newPrincipalAmount;

    const originalCollateralAmount = collateral?.amount ?? 0;
    const nextCollateralAmount = isFullRepay ? 0 : newCollateralAmount;

    const principalSymbol = BsMetaUtil.getSymbol(loanExpanded.principalMetadata, { shortSymbol: true });
    const collateralSymbol = BsMetaUtil.getSymbol(collateral?.metadata, { shortSymbol: true });

    const calculatedLoanHealth = calculateLoanHealth(loanExpanded);
    const strategyLtvBase = getZcLoanLtv(loanExpanded);

    const currentLiquidationThreshold = calculatedLoanHealth?.liquidationThreshold;
    const effectiveLiquidationThreshold = newLiquidationThreshold ?? currentLiquidationThreshold;
    const effectiveStrategyLtv = newStrategyLtv ?? strategyLtvBase;

    const newDebtUsd = bsMath.mul(newPrincipalAmount, principalUsdOraclePrice) ?? 0;

    const totalCollateralValueUsd = getLoanCollateralMarketUsdValue(loanExpanded);
    const hasMultipleCollateral = (loanExpanded?.collateral.length ?? 0) > 1;
    const loanCollateralValue = hasMultipleCollateral
        ? totalCollateralValueUsd
        : calculatedLoanHealth?.loanCollateralValue ?? 0;
    const nextCollateralUsdValue =
        newCollateralValueUsd ??
        (hasMultipleCollateral
            ? loanCollateralValue
            : collateralUsdOraclePrice && newCollateralAmount !== undefined
            ? bsMath.mul(newCollateralAmount, collateralUsdOraclePrice) ?? 0
            : loanCollateralValue);

    const originalLtv = calculatedLoanHealth?.ltv;
    const nextLtv = nextCollateralUsdValue > 0 ? newDebtUsd / nextCollateralUsdValue : newDebtUsd > 0 ? Infinity : 0;

    const originalHealth = calculatedLoanHealth
        ? {
              health: calculatedLoanHealth.health,
              loanHealthStatus: calculatedLoanHealth.loanHealthStatus,
              ltv: calculatedLoanHealth.ltv
          }
        : undefined;

    const nextHealthRatio =
        effectiveLiquidationThreshold !== undefined
            ? calculateHealthRatio(newDebtUsd, nextCollateralUsdValue, effectiveLiquidationThreshold)
            : undefined;

    const nextHealthStatus =
        nextLtv !== undefined && effectiveStrategyLtv !== undefined && effectiveLiquidationThreshold !== undefined
            ? getHealthStatus(nextLtv, effectiveStrategyLtv, effectiveLiquidationThreshold)
            : undefined;

    const newHealth =
        nextHealthRatio !== undefined && nextHealthStatus !== undefined && nextLtv !== undefined
            ? { health: nextHealthRatio, loanHealthStatus: nextHealthStatus, ltv: nextLtv }
            : undefined;

    const originalBorrowApy = ledger?.apy ?? 0;
    const collateralChange = nextCollateralAmount !== originalCollateralAmount;
    const debtChange = newDebt !== originalDebt;

    const collateralApy = loanExpanded?.collateralYieldPct;
    const originalNetApy = loanExpanded?.netApyLive;

    const collateralUsdValueForNetApy = bsMath.mul(nextCollateralAmount, collateralUsdOraclePrice);
    const totalDebtUsdValueForNetApy = bsMath.mul(newDebt, principalUsdOraclePrice);

    const newNetApy =
        newBorrowApy || collateralChange || debtChange
            ? calculateNetApyLive({
                  loanCollateral: collateral,
                  collateralApyPct: collateralApy,
                  principalApyPct: newBorrowApy ?? originalBorrowApy,
                  collateralUsdValue: collateralUsdValueForNetApy,
                  totalDebtUsdValue: totalDebtUsdValueForNetApy,
                  principalYieldPct: loanExpanded?.principalYieldPct
              })
            : originalNetApy;

    const originalLiquidationPrice = calculateLiquidationPrice(
        originalDebt,
        originalCollateralAmount,
        effectiveLiquidationThreshold
    );
    const newLiquidationPrice = calculateLiquidationPrice(newDebt, nextCollateralAmount, effectiveLiquidationThreshold);

    return {
        originalDebt,
        newDebt,
        principalSymbol,
        originalCollateralAmount,
        newCollateralAmount: nextCollateralAmount,
        collateralSymbol,
        originalCollateralValueUsd: loanCollateralValue,
        newCollateralValueUsd: nextCollateralUsdValue,
        displayCollateralAsUsd: !!newCollateralValueUsd || hasMultipleCollateral,
        newDueDate,

        originalHealth,
        newHealth,
        originalLtv,
        newLtv: nextLtv,
        liquidationThreshold: effectiveLiquidationThreshold,
        strategyLtv: effectiveStrategyLtv,
        originalLiquidationPrice,
        newLiquidationPrice,
        principalUsdPrice: principalUsdOraclePrice,

        originalBorrowApy,
        newBorrowApy,
        collateralApy,
        originalNetApy,
        newNetApy
    };
}

type ChangeValueProps = {
    originalValue: string | undefined;
    newValue: string | undefined;
    showTransitionOnNoDiff?: boolean;
    variant?: TextVariant;
    alwaysShowArrow?: boolean;
    equalCaptionForNew?: boolean;
    loading?: boolean;
};

function ChangeValue({
    originalValue,
    newValue,
    showTransitionOnNoDiff = false,
    variant,
    alwaysShowArrow = false,
    equalCaptionForNew = false,
    loading = false
}: ChangeValueProps) {
    const valuesEqual = originalValue === newValue;

    if (valuesEqual && !showTransitionOnNoDiff && !alwaysShowArrow) {
        return (
            <Text variant={variant} color="body" loading={loading}>
                {originalValue}
            </Text>
        );
    }

    const newColor = valuesEqual && equalCaptionForNew ? "caption" : "body";

    return (
        <Row sx={{ minWidth: "max-content" }} spacing={0.5}>
            <Text variant={variant} color="caption" loading={loading}>
                {originalValue}
            </Text>
            <Text color="disabled" variant={iconAndChevronVariant}>
                <ArrowForwardOutlined />
            </Text>
            <Text variant={variant} color={newColor} loading={loading}>
                {newValue}
            </Text>
        </Row>
    );
}
